import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { API_URLS } from '../../../config/api';
import { formatPrice, normalizeUrl } from '../../../utils/format';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search,
  Filter,
  Palette,
  X,
  Tag as TagIcon,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Image,
  Camera,
  Loader2
} from 'lucide-react';

const AdminWebsiteTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ecommerceCategories, setEcommerceCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [mediaFiles, setMediaFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    category_ids: [],
    image_url: '',
    demo_url: '',
    admin_url: '',
    purchase_url: '',
    price: '',
    tags: [],
    is_featured: false,
    is_active: true
  });
  const [capturingScreenshots, setCapturingScreenshots] = useState(new Set());
  const [bulkScreenshotProgress, setBulkScreenshotProgress] = useState(null);
  const [tagFormData, setTagFormData] = useState({
    name: '',
    description: '',
    color: '#6B46C1',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchEcommerceCategories();
    fetchTags();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URLS.ADMIN.WEBSITE_TEMPLATES);
      const result = await response.json();
      
      if (result.status === 'success') {
        setTemplates(result.data || []);
      } else {
        setMessage('Tasarımlar yüklenemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.WEBSITE_TEMPLATE_CATEGORIES);
      
      const responseText = await response.text();
      
      try {
        const result = JSON.parse(responseText);
        
        if (result.status === 'success') {
          setCategories(result.data || []);
        }
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Response was not JSON:', responseText);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchEcommerceCategories = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.ECOMMERCE_DESIGN_CATEGORIES);
      const result = await response.json();
      if (result.status === 'success') {
        setEcommerceCategories(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching ecommerce categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.WEBSITE_TEMPLATE_TAGS);
      const result = await response.json();
      
      if (result.status === 'success') {
        setTags(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleCreateTag = () => {
    setEditingTag(null);
    setTagFormData({
      name: '',
      description: '',
      color: '#6B46C1',
      sort_order: 0,
      is_active: true
    });
    setShowTagModal(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagFormData({
      name: tag.name || '',
      description: tag.description || '',
      color: tag.color || '#6B46C1',
      sort_order: tag.sort_order || 0,
      is_active: tag.is_active !== false
    });
    setShowTagModal(true);
  };

  const handleSaveTag = async () => {
    try {
      const url = editingTag 
        ? `${API_URLS.ADMIN.UPDATE_WEBSITE_TEMPLATE_TAG}&id=${editingTag.id}`
        : API_URLS.ADMIN.CREATE_WEBSITE_TEMPLATE_TAG;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tagFormData)
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage(editingTag ? 'Tag güncellendi' : 'Tag oluşturuldu');
        setMessageType('success');
        setShowTagModal(false);
        fetchTags();
      } else {
        setMessage(result.message || 'Tag kaydedilemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Bu tag\'i silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URLS.ADMIN.DELETE_WEBSITE_TEMPLATE_TAG}&id=${tagId}`, {
        method: 'POST'
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Tag silindi');
        setMessageType('success');
        fetchTags();
      } else {
        setMessage(result.message || 'Tag silinemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleToggleTagStatus = async (tagId) => {
    try {
      const response = await fetch(API_URLS.ADMIN.TOGGLE_WEBSITE_TEMPLATE_TAG_STATUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: tagId })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Tag durumu güncellendi');
        setMessageType('success');
        fetchTags();
      } else {
        setMessage(result.message || 'Durum güncellenemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleToggleStatus = async (templateId, currentStatus) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=toggleWebsiteTemplateStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: templateId })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Tasarım durumu güncellendi');
        setMessageType('success');
        fetchTemplates();
      } else {
        setMessage(result.message || 'Durum güncellenemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Bu tasarımı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteWebsiteTemplate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: templateId })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Tasarım silindi');
        setMessageType('success');
        fetchTemplates();
      } else {
        setMessage(result.message || 'Tasarım silinemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    
    // Parse tags if they exist
    let templateTags = [];
    if (template.tags) {
      if (typeof template.tags === 'string') {
        try {
          templateTags = JSON.parse(template.tags);
        } catch {
          templateTags = [];
        }
      } else if (Array.isArray(template.tags)) {
        templateTags = template.tags;
      }
    }
    
    // Parse category_ids if they exist (from pivot table or JSON)
    let templateCategoryIds = [];
    if (template.category_ids) {
      if (Array.isArray(template.category_ids)) {
        templateCategoryIds = template.category_ids;
      } else if (typeof template.category_ids === 'string') {
        try {
          templateCategoryIds = JSON.parse(template.category_ids);
        } catch {
          templateCategoryIds = [];
        }
      }
    }
    
    setFormData({
      name: template.name || '',
      description: template.description || '',
      category: template.category || '',
      category_ids: templateCategoryIds,
      image_url: template.image_url || '',
      demo_url: template.demo_url || '',
      admin_url: template.admin_url || '',
      purchase_url: template.purchase_url || '',
      price: template.price || '',
      tags: templateTags,
      is_featured: template.is_featured || false,
      is_active: template.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleView = async (template) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getWebsiteTemplate&id=${template.id}`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setSelectedTemplate(result.data);
        setIsDetailModalOpen(true);
        return;
      }
      
      setMessage('Tasarım detayları alınamadı');
      setMessageType('error');
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      category_ids: [],
      image_url: '',
      demo_url: '',
      admin_url: '',
      purchase_url: '',
      price: '',
      tags: [],
      is_featured: false,
      is_active: true
    });
  };

  const handleToggleTag = (tagName) => {
    setFormData(prev => {
      const currentTags = prev.tags || [];
      const hasTag = currentTags.includes(tagName);
      
      return {
        ...prev,
        tags: hasTag 
          ? currentTags.filter(t => t !== tagName)
          : [...currentTags, tagName]
      };
    });
  };

  const captureScreenshot = async (template, force = false) => {
    const url = template.demo_url || template.admin_url;
    if (!url) {
      alert('Bu tasarım için demo URL veya admin URL bulunamadı');
      return;
    }

    const normalizedUrl = normalizeUrl(url);
    setCapturingScreenshots(prev => new Set(prev).add(template.id));
    
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=captureDesignScreenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          design_id: template.id,
          design_type: 'website',
          url: normalizedUrl,
          force: force
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setTemplates(prevTemplates => 
          prevTemplates.map(t => 
            t.id === template.id 
              ? { ...t, image_url: result.data.image_url }
              : t
          )
        );
        setMessage('Ekran görüntüsü başarıyla alındı ve kaydedildi!');
        setMessageType('success');
      } else {
        setMessage('Ekran görüntüsü alınamadı: ' + (result.message || 'Bilinmeyen hata'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Screenshot capture error:', error);
      setMessage('Ekran görüntüsü alınırken bir hata oluştu: ' + error.message);
      setMessageType('error');
    } finally {
      setCapturingScreenshots(prev => {
        const newSet = new Set(prev);
        newSet.delete(template.id);
        return newSet;
      });
    }
  };

  const captureBulkScreenshots = async () => {
    const templatesToCapture = templates.filter(template => {
      const url = template.demo_url || template.admin_url;
      return url;
    });

    if (templatesToCapture.length === 0) {
      setMessage('Ekran görüntüsü alınabilecek tasarım bulunamadı (demo URL veya admin URL gerekli)');
      setMessageType('error');
      return;
    }

    if (!confirm(`${templatesToCapture.length} tasarım için ekran görüntüsü alınacak. Devam etmek istiyor musunuz?`)) {
      return;
    }

    setBulkScreenshotProgress({
      total: templatesToCapture.length,
      current: 0,
      completed: 0,
      failed: 0
    });

    for (let i = 0; i < templatesToCapture.length; i++) {
      const template = templatesToCapture[i];
      setBulkScreenshotProgress(prev => ({
        ...prev,
        current: i + 1
      }));

      try {
        const url = template.demo_url || template.admin_url;
        const normalizedUrl = normalizeUrl(url);
        const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=captureDesignScreenshot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify({
            design_id: template.id,
            design_type: 'website',
            url: normalizedUrl,
            force: false
          })
        });

        const result = await response.json();
        
        if (result.status === 'success') {
          setTemplates(prevTemplates => 
            prevTemplates.map(t => 
              t.id === template.id 
                ? { ...t, image_url: result.data.image_url }
                : t
            )
          );
          setBulkScreenshotProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
        } else {
          setBulkScreenshotProgress(prev => ({
            ...prev,
            failed: prev.failed + 1
          }));
        }
      } catch (error) {
        console.error(`Screenshot capture error for template ${template.id}:`, error);
        setBulkScreenshotProgress(prev => ({
          ...prev,
          failed: prev.failed + 1
        }));
      }

      if (i < templatesToCapture.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setBulkScreenshotProgress(prev => {
      setMessage(`Toplu ekran görüntüsü alma tamamlandı! Başarılı: ${prev.completed}, Başarısız: ${prev.failed}`);
      setMessageType('success');
      return null;
    });
  };

  // Build hierarchical category tree
  const buildCategoryTree = (catList) => {
    const categoryMap = {};
    const rootCategories = [];
    
    // Create map
    catList.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    
    // Build tree
    catList.forEach(cat => {
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
      } else {
        rootCategories.push(categoryMap[cat.id]);
      }
    });
    
    return rootCategories;
  };

  // Get categories to use (website template categories if they have tree structure, otherwise ecommerce categories)
  const getCategoriesToUse = () => {
    // Check if website template categories have parent_id (tree structure)
    const hasTreeStructure = categories.some(cat => cat.parent_id);
    if (hasTreeStructure && categories.length > 0) {
      return categories;
    }
    // Use ecommerce categories as fallback
    return ecommerceCategories.length > 0 ? ecommerceCategories : categories;
  };

  const categoriesToUse = getCategoriesToUse();
  const categoryTree = buildCategoryTree(categoriesToUse);

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleToggleCategory = (categoryId) => {
    setFormData(prev => {
      const currentIds = prev.category_ids || [];
      const hasCategory = currentIds.includes(categoryId);
      
      return {
        ...prev,
        category_ids: hasCategory 
          ? currentIds.filter(id => id !== categoryId)
          : [...currentIds, categoryId]
      };
    });
  };

  const fetchMediaFiles = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.MEDIA);
      const data = await response.json();
      
      if (data.status === 'success') {
        setMediaFiles(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching media files:', error);
    }
  };

  const openMediaLibrary = () => {
    fetchMediaFiles();
    setIsMediaModalOpen(true);
  };

  const handleMediaSelect = (mediaUrl) => {
    setFormData({ ...formData, image_url: mediaUrl });
    setIsMediaModalOpen(false);
  };

  // Render category tree recursively
  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const isSelected = (formData.category_ids || []).includes(category.id);
    
    return (
      <div key={category.id} className="mb-1">
        <div className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 ${level > 0 ? 'ml-4' : ''}`}>
          {hasChildren && (
            <button
              type="button"
              onClick={() => toggleCategory(category.id)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          {!hasChildren && <div className="w-6" />}
          <button
            type="button"
            onClick={() => handleToggleCategory(category.id)}
            className={`flex-1 text-left px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              isSelected
                ? 'bg-purple-600 text-white ring-2 ring-purple-500'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {category.name}
          </button>
        </div>
        {hasChildren && isExpanded && (
          <div className="ml-6">
            {category.children.map(child => renderCategoryTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleSaveTemplate = async () => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateWebsiteTemplate&id=${editingTemplate.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Tasarım güncellendi');
        setMessageType('success');
        handleCloseModal();
        fetchTemplates();
      } else {
        setMessage(result.message || 'Tasarım güncellenemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
        <Eye className="w-3 h-3" />
        Aktif
      </Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
      <EyeOff className="w-3 h-3" />
      Pasif
    </Badge>;
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Tasarımlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kolay Site Tasarımları</h1>
          <p className="text-gray-600">Website tasarımlarını yönetin ve düzenleyin</p>
        </div>
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-600">{templates.length} tasarım</span>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Tag Management Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TagIcon className="w-5 h-5 text-purple-600" />
              <CardTitle>Tag Yönetimi</CardTitle>
            </div>
            <Button onClick={handleCreateTag} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Tag
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {tags.length === 0 ? (
              <p className="text-sm text-gray-500">Henüz tag eklenmemiş</p>
            ) : (
              tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-shadow"
                  style={{ borderLeftColor: tag.color, borderLeftWidth: '3px' }}
                >
                  <Badge style={{ backgroundColor: tag.color }} className="text-white">
                    {tag.name}
                  </Badge>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditTag(tag)}
                      className="h-6 w-6 p-0"
                      title="Düzenle"
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleTagStatus(tag.id)}
                      className="h-6 w-6 p-0"
                      title={tag.is_active ? "Pasif Yap" : "Aktif Yap"}
                    >
                      {tag.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTag(tag.id)}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                      title="Sil"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Tasarım ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="sm:w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Tüm Kategoriler</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button 
                onClick={captureBulkScreenshots} 
                size="sm"
                variant="outline"
                disabled={bulkScreenshotProgress !== null}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                {bulkScreenshotProgress ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    İşleniyor ({bulkScreenshotProgress.current}/{bulkScreenshotProgress.total})
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    Toplu Ekran Görüntüsü Al
                  </>
                )}
              </Button>
            </div>
            {bulkScreenshotProgress && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-700">İlerleme:</span>
                  <span className="text-sm font-medium text-blue-700">
                    {bulkScreenshotProgress.completed} başarılı, {bulkScreenshotProgress.failed} başarısız
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkScreenshotProgress.current / bulkScreenshotProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <Card key={template.id} className="overflow-hidden">
            <div className="aspect-video bg-gray-100">
              {template.image_url ? (
                <img
                  src={template.image_url}
                  alt={template.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Palette className="w-12 h-12 text-gray-400" />
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{template.name}</h3>
                {getStatusBadge(template.is_active)}
              </div>
              {template.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {template.description}
                </p>
              )}
              {template.category && (
                <Badge variant="outline" className="mb-3">
                  {template.category}
                </Badge>
              )}
              <div className="text-sm text-gray-600 space-y-1 mb-3">
                {template.price && (
                  <div>
                    <span className="font-medium">Fiyat:</span> <span className="text-purple-600 font-bold">{formatPrice(template.price)}</span>
                  </div>
                )}
                {template.demo_url && (
                  <div className="text-xs">
                    <span className="font-medium">Demo:</span> <a href={normalizeUrl(template.demo_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[200px]">{template.demo_url}</a>
                  </div>
                )}
                {template.admin_url && (
                  <div className="text-xs">
                    <span className="font-medium">Admin:</span> <a href={normalizeUrl(template.admin_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[200px]">{template.admin_url}</a>
                  </div>
                )}
                {template.purchase_url && (
                  <div className="text-xs">
                    <span className="font-medium">Satın Al:</span>{' '}
                    <a
                      href={normalizeUrl(template.purchase_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate block max-w-[200px]"
                    >
                      {template.purchase_url}
                    </a>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleView(template)}
                    title="Görüntüle"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(template)}
                    title="Düzenle"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleStatus(template.id, template.is_active)}
                    title={template.is_active ? "Pasif Yap" : "Aktif Yap"}
                  >
                    {template.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(template.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {(template.demo_url || template.admin_url) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => captureScreenshot(template, false)}
                    disabled={capturingScreenshots.has(template.id)}
                    className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                  >
                    {capturingScreenshots.has(template.id) ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Alınıyor...
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 mr-1" />
                        Ekran Görüntüsü Al
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Palette className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Henüz tasarım bulunmuyor</p>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Tasarım Düzenle</h2>
              <Button variant="ghost" onClick={handleCloseModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tasarım Adı</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kategoriler
                </label>
                <div className="p-3 border border-gray-300 rounded-lg bg-gray-50 max-h-64 overflow-y-auto">
                  {categoriesToUse.length === 0 ? (
                    <p className="text-sm text-gray-500">Önce kategori oluşturun</p>
                  ) : (
                    <div>
                      {categoryTree.map(category => renderCategoryTree(category))}
                    </div>
                  )}
                </div>
                {formData.category_ids && formData.category_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1 break-words">
                    Seçili: {formData.category_ids.map(id => {
                      const cat = categoriesToUse.find(c => c.id === id);
                      return cat ? cat.name : id;
                    }).join(', ')}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resim URL</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openMediaLibrary}
                    className="px-3 w-full sm:w-auto"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Medya Kütüphanesi
                  </Button>
                </div>
                {formData.image_url && (
                  <div className="mt-2">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-32 h-20 object-cover rounded border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Demo URL</label>
                <input
                  type="url"
                  value={formData.demo_url}
                  onChange={(e) => setFormData({...formData, demo_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin URL</label>
                <input
                  type="url"
                  value={formData.admin_url}
                  onChange={(e) => setFormData({...formData, admin_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="https://admin.example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Satın Al URL</label>
                <input
                  type="url"
                  value={formData.purchase_url}
                  onChange={(e) => setFormData({ ...formData, purchase_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="https://satis.example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fiyat (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <TagIcon className="w-4 h-4 inline mr-1" />
                  Tag'ler
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50">
                  {tags.length === 0 ? (
                    <p className="text-sm text-gray-500">Önce tag oluşturun</p>
                  ) : (
                    tags.filter(tag => tag.is_active).map((tag) => {
                      const isSelected = (formData.tags || []).includes(tag.name);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleToggleTag(tag.name)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'ring-2 ring-purple-500'
                              : 'hover:ring-2 hover:ring-gray-300'
                          }`}
                          style={{
                            backgroundColor: isSelected ? tag.color : '#f3f4f6',
                            color: isSelected ? 'white' : '#374151'
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })
                  )}
                </div>
                {formData.tags && formData.tags.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Seçili: {formData.tags.join(', ')}
                  </p>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Öne Çıkan</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Aktif</span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={handleCloseModal}>
                İptal
              </Button>
              <Button onClick={handleSaveTemplate}>
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Media Library Modal */}
      {isMediaModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Medya Kütüphanesi</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMediaModalOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-96 overflow-y-auto">
              {mediaFiles
                .filter(file => {
                  // Only show image files
                  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
                  const fileName = file.name.toLowerCase();
                  return imageExtensions.some(ext => fileName.endsWith(ext));
                })
                .length > 0 ? (
                  mediaFiles
                    .filter(file => {
                      // Only show image files
                      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
                      const fileName = file.name.toLowerCase();
                      return imageExtensions.some(ext => fileName.endsWith(ext));
                    })
                    .map((file) => (
                    <div
                      key={file.name}
                      className="cursor-pointer border rounded-lg p-2 hover:bg-gray-50"
                      onClick={() => handleMediaSelect(file.url)}
                    >
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-20 object-cover rounded"
                      />
                      <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full flex flex-col items-center justify-center py-8 text-gray-500">
                    <Image className="w-12 h-12 mb-2" />
                    <p>Medya kütüphanesinde resim bulunamadı</p>
                    <p className="text-sm">Lütfen önce medya kütüphanesine resim yükleyin</p>
                  </div>
                )}
            </div>

            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                onClick={() => setIsMediaModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <TagIcon className="w-5 h-5" />
                {editingTag ? 'Tag Düzenle' : 'Yeni Tag'}
              </h2>
              <Button variant="ghost" onClick={() => setShowTagModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Adı *</label>
                <input
                  type="text"
                  value={tagFormData.name}
                  onChange={(e) => setTagFormData({...tagFormData, name: e.target.value})}
                  placeholder="Örn: Modern, Minimalist, Renkli"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea
                  value={tagFormData.description}
                  onChange={(e) => setTagFormData({...tagFormData, description: e.target.value})}
                  rows={2}
                  placeholder="Tag hakkında kısa açıklama (opsiyonel)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Renk</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={tagFormData.color}
                    onChange={(e) => setTagFormData({...tagFormData, color: e.target.value})}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tagFormData.color}
                    onChange={(e) => setTagFormData({...tagFormData, color: e.target.value})}
                    placeholder="#6B46C1"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <div 
                    className="w-10 h-10 rounded-lg border border-gray-300"
                    style={{ backgroundColor: tagFormData.color }}
                  ></div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                <input
                  type="number"
                  value={tagFormData.sort_order}
                  onChange={(e) => setTagFormData({...tagFormData, sort_order: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={tagFormData.is_active}
                    onChange={(e) => setTagFormData({...tagFormData, is_active: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Aktif</span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => setShowTagModal(false)}>
                İptal
              </Button>
              <Button onClick={handleSaveTag}>
                <TagIcon className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebsiteTemplates;
